// Main Algolia Search Implementation
class AlgoliaSearch {
  constructor() {
    this.searchManager = null;
    this.filterManager = null;
    this.uiManager = null;
    this.urlManager = null;
    this.initialized = false;
    // Add pubsub unsubscriber
    this.deliveryStoreUnsubscriber = null;
    // Cache DOM elements to avoid repeated queries
    this.cachedElements = {
      sortElements: null,
      searchInput: null,
      searchResetButton: null,
      productGrid: null,
      facetContainer: null,
    };
    // Debounced methods
    this.debouncedSearch = this.debounce(this.handleSearch.bind(this), 300);
    this.debouncedRender = this.debounce(this.performRender.bind(this), 16);
  }

  async init() {
    if (this.initialized) return;

    try {
      await this.waitForAlgolia();
      this.initializeComponents();
      this.setupEventListeners();
      this.setupPubSubListeners();
      await this.performInitialSearch();
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Algolia search:', error);
    }
  }

  waitForAlgolia() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Algolia lib not found'));
      }, 5000);

      const checkAlgolia = () => {
        if (window.algoliaShopify?.searchClient) {
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(checkAlgolia, 100);
        }
      };
      checkAlgolia();
    });
  }

  initializeComponents() {
    this.searchManager = new SearchManager(window.algoliaShopify.searchClient);
    this.filterManager = new FilterManager();
    this.uiManager = new UIManager();
    this.urlManager = new URLManager();

    // Load initial state from URL
    this.filterManager.initializeFromURL();

    // Populate sort options dynamically (with delay for collections)
    setTimeout(() => {
      this.populateSortOptions();
    }, 100);
  }

  cacheSortElements() {
    if (!this.cachedElements.sortElements) {
      this.cachedElements.sortElements = [
        document.getElementById('SortBy-search'),
        document.getElementById('SortBy-drawer'),
        document.getElementById('SortBy'),
        document.querySelector('select[name="sort_by"]'),
        ...document.querySelectorAll('select[data-sort-by]'),
        ...document.querySelectorAll('select[class*="sort"]'),
        ...document.querySelectorAll('select[id*="sort"]'),
        ...document.querySelectorAll('select[id*="Sort"]'),
      ]
        .filter(Boolean)
        .filter((element, index, array) => array.indexOf(element) === index);
    }
    return this.cachedElements.sortElements;
  }

  populateSortOptions() {
    const isCollection = !!window.collectionConfig;
    const sortOrders = isCollection
      ? window.algoliaShopify.collectionSortOrders
      : window.algoliaShopify.sortOrders;

    if (!sortOrders || !Array.isArray(sortOrders)) {
      console.warn('Sort orders not found in algoliaShopify config');
      return;
    }

    const sortElements = this.cacheSortElements();

    sortElements.forEach(selectElement => {
      // Clear existing options
      selectElement.innerHTML = '';

      // Create options in fragment first
      const tempFragment = document.createDocumentFragment();
      sortOrders.forEach(sortOrder => {
        const option = document.createElement('option');
        option.value = sortOrder.value;
        option.textContent = sortOrder.label;
        tempFragment.appendChild(option);
      });

      // Single DOM update per select
      selectElement.appendChild(tempFragment);
    });

    // Now update the selected value after options are populated
    this.filterManager.updateSortSelectors(this.filterManager.getSortBy());
  }

  cacheSearchElements() {
    if (!this.cachedElements.searchInput) {
      this.cachedElements.searchInput = document.querySelector(
        '#MainContent .search__input.form-control'
      );
    }
    if (!this.cachedElements.searchResetButton) {
      this.cachedElements.searchResetButton = document.querySelector('#MainContent .search__reset');
    }
    return {
      searchInput: this.cachedElements.searchInput,
      searchResetButton: this.cachedElements.searchResetButton,
    };
  }

  setupEventListeners() {
    const { searchInput, searchResetButton } = this.cacheSearchElements();

    // Search input with debounced handler
    if (searchInput) {
      searchInput.addEventListener('input', event => {
        this.debouncedSearch(event.target.value);
      });
    }

    if (searchResetButton) {
      searchResetButton.addEventListener('click', e => {
        e.preventDefault();
        searchInput.value = '';
        searchInput.focus();
        this.handleSearch('');
      });
    }

    // Sort elements - use cached elements
    const sortElements = this.cacheSortElements();
    sortElements.forEach(element => {
      element.addEventListener('change', event => this.handleSortChange(event.target.value));
    });

    // Use event delegation for filter changes
    this.setupGlobalEventDelegation();
  }

  setupGlobalEventDelegation() {
    // Single global event listener using delegation
    document.addEventListener(
      'change',
      event => {
        if (this.isFilterInput(event.target)) {
          this.handleFilterChange(event.target);
        }
      },
      { passive: true }
    );
  }

  setupPubSubListeners() {
    // Subscribe to delivery store updates
    this.deliveryStoreUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.deliveryStoreUpdated,
      this.handleDeliveryStoreUpdate.bind(this)
    );
  }

  async handleDeliveryStoreUpdate(data) {
    try {
      // Remove any click and collect filters from URL and active filters
      this.removeClickAndCollectFilters();

      // Update current store in UI manager
      this.uiManager.updateCurrentStore();

      // Re-execute search to refresh filters with new store
      await this.executeSearch();
    } catch (error) {
      console.error('Failed to handle delivery store update:', error);
    }
  }

  removeClickAndCollectFilters() {
    // Remove from active filters
    const activeFilters = this.filterManager.getActiveFilters();
    Object.keys(activeFilters.facets).forEach(facetName => {
      if (facetName.includes('search_information.click_and_collect')) {
        delete activeFilters.facets[facetName];
      }
    });

    // Update filter manager
    this.filterManager.activeFilters.facets = activeFilters.facets;

    // Remove from URL
    this.urlManager.removeClickAndCollectFromURL();
  }

  async performInitialSearch() {
    const searchElement = document.querySelector('#MainContent .search__input.form-control');
    const hasSearchTerm = searchElement?.value;
    const hasFilters = this.filterManager.hasActiveFilters();
    const isCollection = !!window.collectionConfig;
    const initialPage = this.filterManager.getInitialPage();

    if (hasSearchTerm || hasFilters || isCollection || initialPage > 0) {
      await this.executeSearch(initialPage);
      // Reset initial page after first search to prevent it from persisting
      this.filterManager.resetInitialPage();
    }
  }

  async handleSearch(query) {
    await this.executeSearch();
  }

  handleSortChange(sortValue) {
    this.filterManager.setSortBy(sortValue);
    this.executeSearch();
  }

  handleFilterChange(input) {
    this.filterManager.updateFromInput(input);
    this.executeSearch();
  }

  async executeSearch(page = 0) {
    try {
      // Show shimmer state before starting search
      this.uiManager.showShimmerState();

      const searchParams = this.buildSearchParams(page);

      const results = await this.searchManager.search(searchParams);

      // Use debounced rendering to prevent UI blocking
      this.debouncedRender(results, page, searchParams.hitsPerPage);
      this.urlManager.updateURL(this.filterManager.getActiveFilters(), page);
    } catch (error) {
      console.error('Search execution failed:', error);
      // Hide shimmer state on error
      this.uiManager.hideShimmerState();
    }
  }

  performRender(results, page, hitsPerPage) {
    // Use requestAnimationFrame for smooth rendering
    requestAnimationFrame(() => {
      this.uiManager.renderResults(results, page, hitsPerPage);
    });
  }

  buildSearchParams(page = 0) {
    const searchElement = document.querySelector('#MainContent .search__input.form-control');
    const query = searchElement?.value || '';

    // Get products per page from section settings
    const sectionContainer = document.querySelector('.collection[data-products-per-page]');
    const productsPerPage = sectionContainer
      ? parseInt(sectionContainer.dataset.productsPerPage)
      : 16; // fallback to default

    return {
      query,
      page,
      filters: this.filterManager.buildFilters(),
      facetFilters: this.filterManager.buildFacetFilters(),
      numericFilters: this.filterManager.buildNumericFilters(),
      sortBy: this.filterManager.getSortBy(),
      facets: this.getFacetNames(),
      hitsPerPage: productsPerPage,
    };
  }

  getFacetNames() {
    const facetsConfig = window.collectionConfig
      ? window.algoliaShopify.collectionFacets
      : window.algoliaShopify.facets;

    return facetsConfig
      ? facetsConfig.filter(facet => facet.enabled).map(facet => facet.escapedName)
      : ['vendor', 'product_type', 'tags', 'price_range'];
  }

  isFilterInput(element) {
    // Exclude price inputs since they have their own dedicated handlers
    return element.matches('input[name^="filter.v."]:not([name*="price"]), select[name*="sort"]');
  }

  debounce(func, wait = 150) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  updateSortSelectors(sortBy) {
    // Use cached sort elements
    const sortElements = this.cacheSortElements();

    // Batch DOM updates
    requestAnimationFrame(() => {
      sortElements.forEach(element => {
        if (element) element.value = sortBy;
      });
    });
  }

  // Add cleanup method
  destroy() {
    if (this.deliveryStoreUnsubscriber) {
      this.deliveryStoreUnsubscriber();
    }
  }
}

// Search Manager - Handles Algolia API interactions
class SearchManager {
  constructor(searchClient) {
    this.searchClient = searchClient;
    this.currentIndex = 'shopify_products';
  }

  async search(params) {
    try {
      // Step 1: Get initial hits to extract product IDs
      const initialResults = await this.getInitialHits(params);

      if (initialResults.hits.length === 0) {
        return this.processResults(initialResults);
      }

      // Step 2: Extract unique product IDs
      const productIds = this.extractProductIds(initialResults.hits);

      // Step 3: Fetch all variants for these products
      const allVariantsResults = await this.getAllVariantsForProducts(productIds, params);

      // Step 4: Get price context to show accurate min/max for current filter set
      let priceContextStats = null;
      // Always get price context when we have other filters active, or when price filter is active
      if (this.hasPriceFilter(params) || this.hasOtherFilters(params)) {
        priceContextStats = await this.getPriceContext(params);
      }

      // Step 5: Combine results, keeping the original hits order and supplementing with additional variants
      const combinedResults = {
        ...initialResults,
        // Keep original hits (correct order) and supplement with additional variants
        hits: initialResults.hits,
        additionalVariants: allVariantsResults.hits,
        // Always use facets from initial search - this gives correct counts for filtering
        facets: initialResults.facets,
        facets_stats: initialResults.facets_stats,
        // Add price context for true max price when price filter is active
        price_context_stats: priceContextStats,
      };

      return this.processResults(combinedResults);
    } catch (error) {
      console.error('Search failed:', error);
      throw error;
    }
  }

  async getInitialHits(params) {
    const indexName = this.getSearchIndex(params.sortBy);
    const searchParams = this.buildAlgoliaParams(params);
    // Use distinct to get one hit per product for initial search
    searchParams.distinct = 1;

    const multipleSearchParams = [
      {
        query: params.query,
        indexName: indexName,
        params: searchParams,
      },
    ];

    // Add additional facet queries for each facet filter
    if (Array.isArray(searchParams.facetFilters) && searchParams.facetFilters.length > 0) {
      searchParams.facetFilters.forEach((filter, idx) => {
        // Helper to extract facet name
        const getFacetName = f => {
          const facetGroup = Array.isArray(f) ? f[0] : f;
          // Split the filter by the first colon to get the facet name e.g vendor:Merida -> vendor
          return facetGroup.split(':')[0];
        };

        // Remove Duplicate Filters
        const filters = searchParams.facetFilters.filter((_, i) => i !== idx);
        const facet = getFacetName(filter);

        multipleSearchParams.push({
          query: params.query,
          indexName: 'shopify_products',
          params: { ...searchParams, facets: facet, facetFilters: filters },
        });
      });
    }

    const multipleResults = await this.searchClient.multipleQueries(multipleSearchParams);

    // The first Result contains the main search data as well as all facets.
    const searchData = multipleResults.results[0];

    if (multipleResults.results.length > 1) {
      const additionalFacets = multipleResults.results.slice(1).reduce((acc, result) => {
        return Object.assign(acc, result.facets);
      }, {});

      searchData.facets = Object.assign({}, searchData.facets, additionalFacets);
    }

    return searchData;
  }

  extractProductIds(hits) {
    const uniqueIds = new Set();
    hits.forEach(hit => {
      if (hit.id) {
        uniqueIds.add(hit.id);
      }
    });
    return Array.from(uniqueIds);
  }

  async getAllVariantsForProducts(productIds, params) {
    if (productIds.length === 0) {
      return { hits: [], facets: {}, facets_stats: {} };
    }

    // Build filter string for all product IDs
    const productIdFilters = productIds.map(id => `_tags:"id:${id}"`).join(' OR ');

    // Use the base index (no sorting) for variant fetching to ensure we get all variants
    const index = this.searchClient.initIndex('shopify_products');

    // For the variant fetch, only filter by product IDs to get ALL variants for selected products
    const searchParams = {
      filters: productIdFilters,
      facets: [], // Don't calculate facets for this search - we use the initial search facets
      hitsPerPage: 1000, // High limit to ensure we get all variants
      distinct: false,
    };

    const results = await index.search('', searchParams);

    // Sort the results to match the original product order
    const sortedHits = this.sortVariantsByOriginalOrder(results.hits, productIds);

    return {
      ...results,
      hits: sortedHits,
    };
  }

  sortVariantsByOriginalOrder(variants, productIds) {
    // Create a map of product ID to its index in the original order
    const orderMap = new Map();
    productIds.forEach((id, index) => {
      orderMap.set(id, index);
    });

    // Group variants by product ID
    const variantsByProduct = new Map();
    variants.forEach(variant => {
      const productId = variant.id;
      if (!variantsByProduct.has(productId)) {
        variantsByProduct.set(productId, []);
      }
      variantsByProduct.get(productId).push(variant);
    });

    // Sort variants within each product by position
    variantsByProduct.forEach(variants => {
      variants.sort((a, b) => a.position - b.position);
    });

    // Build final sorted array following original product order
    const sortedVariants = [];
    productIds.forEach(productId => {
      const productVariants = variantsByProduct.get(productId);
      if (productVariants) {
        sortedVariants.push(...productVariants);
      }
    });

    return sortedVariants;
  }

  hasPriceFilter(params) {
    return params.numericFilters && params.numericFilters.length > 0;
  }

  hasOtherFilters(params) {
    // Check if we have any facet filters (brand, category, etc.) or search query
    return (
      (params.facetFilters && params.facetFilters.length > 0) ||
      (params.query && params.query.trim().length > 0) ||
      window.collectionConfig?.handle // Collection page
    );
  }

  async getPriceContext(params) {
    try {
      const indexName = this.getSearchIndex(params.sortBy);

      // Build search params without numeric filters (price filters)
      const contextParams = this.buildAlgoliaParams({
        ...params,
        numericFilters: [], // Remove price filters
      });

      // Only request price facet stats, no hits needed
      contextParams.hitsPerPage = 0;
      contextParams.facets = ['price'];

      const contextResults = await this.searchClient
        .initIndex(indexName)
        .search(params.query || '', contextParams);

      return contextResults.facets_stats?.price || null;
    } catch (error) {
      console.error('Failed to get price context:', error);
      return null;
    }
  }

  getSearchIndex(sortBy) {
    // If sortBy is already an index name (from the new config), use it directly
    if (sortBy && sortBy.startsWith('shopify_products')) {
      return sortBy;
    }

    // Fallback for legacy sort values
    let indexName = 'shopify_products';

    switch (sortBy) {
      case 'price-ascending':
        indexName = 'shopify_products_price_asc';
        break;
      case 'price-descending':
        indexName = 'shopify_products_price_desc';
        break;
      case 'relevance':
      default:
        indexName = 'shopify_products';
        break;
    }

    return indexName;
  }

  buildAlgoliaParams(params) {
    const algoliaParams = {
      filters: 'inventory_available:true',
      facets: params.facets,
      page: params.page,
      hitsPerPage: params.hitsPerPage,
      maxValuesPerFacet: window.algoliaShopify?.config?.default_facet_param_limit || 10,
      distinct: params.distinct || 10,
      clickAnalytics: true, //99B 20250929 -PO, get queryID from initial search for analytics
    };

    // Add collection filter
    if (window.collectionConfig?.handle) {
      algoliaParams.filters += ` AND collections:${window.collectionConfig.handle}`;
    }

    // Add additional filters
    if (params.filters) {
      algoliaParams.filters += ` AND ${params.filters}`;
    }

    if (params.facetFilters?.length > 0) {
      algoliaParams.facetFilters = params.facetFilters;
    }

    if (params.numericFilters?.length > 0) {
      algoliaParams.numericFilters = params.numericFilters;
    }

    return algoliaParams;
  }

  processResults(results) {
    // Group variants by product ID from the additional variants search
    const additionalVariantsByProduct = new Map();

    if (results.additionalVariants) {
      results.additionalVariants.forEach(variant => {
        const productId = variant.id;
        if (!additionalVariantsByProduct.has(productId)) {
          additionalVariantsByProduct.set(productId, []);
        }
        additionalVariantsByProduct.get(productId).push(variant);
      });
    }

    // Process the original hits (which are in correct order) and supplement with additional variants
    const groupedHits = results.hits.map(hit => {
      const productId = hit.id;
      const additionalVariants = additionalVariantsByProduct.get(productId) || [];

      // Combine the original hit with additional variants, removing duplicates
      const allVariants = [hit];
      additionalVariants.forEach(variant => {
        // Only add if we don't already have this variant
        if (!allVariants.some(existing => existing.objectID === variant.objectID)) {
          allVariants.push(variant);
        }
      });

      // Sort variants by position
      allVariants.sort((a, b) => a.position - b.position);

      const { metafields, ...productData } = hit;
      return {
        ...productData,
        variants: allVariants,
      };
    });

    return {
      ...results,
      groupedHits,
    };
  }
}

// Filter Manager - Handles filter state and logic
class FilterManager {
  constructor() {
    // Set default sort based on page type
    const isCollection = !!window.collectionConfig;
    const defaultSort = isCollection
      ? 'shopify_products' /* 'shopify_products_recently_ordered_count_desc' */
      : 'shopify_products';

    this.activeFilters = {
      priceMin: null,
      priceMax: null,
      facets: {},
      sortBy: defaultSort,
    };
    // Add initial page tracking
    this.initialPage = 0;
  }

  initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    // Sort parameter
    const sortBy = urlParams.get('sort_by');
    if (sortBy) {
      this.activeFilters.sortBy = sortBy;
    }

    // Page parameter - convert from 1-based to 0-based indexing
    const page = urlParams.get('page');
    if (page) {
      this.initialPage = Math.max(0, parseInt(page) - 1);
    }

    // Price range
    this.initializePriceFromURL(urlParams);

    // Facet filters
    this.initializeFacetsFromURL();
  }

  initializePriceFromURL(urlParams) {
    const priceRange = urlParams.get('range[price]');
    if (priceRange) {
      // Handle format: "minValue:maxValue" or ":maxValue" (min not specified)
      const match = priceRange.match(/^(\d+)?:(\d+)$/);
      if (match) {
        // If min value exists (first capture group), set it
        if (match[1]) {
          this.activeFilters.priceMin = parseInt(match[1]);
        }
        // Set max value (second capture group)
        if (match[2]) {
          this.activeFilters.priceMax = parseInt(match[2]);
        }
      }
    }
  }

  initializeFacetsFromURL() {
    const rawQuery = window.location.search;
    const paramRegex = /[?&]filter\.v\.([^=]+)=([^&]+)/g;
    let match;

    while ((match = paramRegex.exec(rawQuery)) !== null) {
      const facetName = match[1];
      const rawValue = match[2].replace(/\+/g, ' ');

      try {
        const decodedValue = decodeURIComponent(rawValue);
        // Split comma-separated values into individual filter values
        const values = decodedValue
          .split(',')
          .map(val => val.trim())
          .filter(val => val.length > 0);

        this.activeFilters.facets[facetName] = this.activeFilters.facets[facetName] || [];
        this.activeFilters.facets[facetName].push(...values);
      } catch (e) {
        console.error('Error decoding URL parameter:', e);
      }
    }
  }

  updateSortSelectors(sortBy) {
    // Use cached sort elements from the main AlgoliaSearch instance
    if (window.algoliaSearch?.cacheSortElements) {
      const sortElements = window.algoliaSearch.cacheSortElements();

      // Batch DOM updates
      requestAnimationFrame(() => {
        sortElements.forEach(element => {
          if (element) element.value = sortBy;
        });
      });
    }
  }

  updateFromInput(input) {
    if (input.type === 'checkbox') {
      this.updateFacetFilter(input);
    } else if (input.type === 'range' || input.name?.includes('price')) {
      this.updatePriceFilter(input);
    }
  }

  updateFacetFilter(checkbox) {
    const facetName = checkbox.name.replace('filter.v.', '');

    if (!this.activeFilters.facets[facetName]) {
      this.activeFilters.facets[facetName] = [];
    }

    if (checkbox.checked) {
      if (!this.activeFilters.facets[facetName].includes(checkbox.value)) {
        this.activeFilters.facets[facetName].push(checkbox.value);
      }
    } else {
      this.activeFilters.facets[facetName] = this.activeFilters.facets[facetName].filter(
        value => value !== checkbox.value
      );

      if (this.activeFilters.facets[facetName].length === 0) {
        delete this.activeFilters.facets[facetName];
      }
    }

    // Price filters are now preserved when facet filters change
  }

  updatePriceFilter(input) {
    if (input.name === 'filter.v.price.gte') {
      this.activeFilters.priceMin = parseInt(input.value);
    } else if (input.name === 'filter.v.price.lte') {
      this.activeFilters.priceMax = parseInt(input.value);
    }
  }

  setSortBy(sortBy) {
    this.activeFilters.sortBy = sortBy;
    this.updateSortSelectors(sortBy);
  }

  getSortBy() {
    return this.activeFilters.sortBy;
  }

  getActiveFilters() {
    return { ...this.activeFilters };
  }

  hasActiveFilters() {
    return (
      Object.keys(this.activeFilters.facets).length > 0 ||
      this.activeFilters.priceMax !== null ||
      this.activeFilters.priceMin !== null
    );
  }

  buildFilters() {
    // Base inventory filter is handled in SearchManager
    return null;
  }

  buildFacetFilters() {
    const facetFilters = [];

    Object.entries(this.activeFilters.facets).forEach(([facet, values]) => {
      if (values.length === 1) {
        facetFilters.push(`${facet}:${this.escapeFilterValue(values[0])}`);
      } else if (values.length > 1) {
        const facetOrGroup = values.map(val => `${facet}:${this.escapeFilterValue(val)}`);
        facetFilters.push(facetOrGroup);
      }
    });

    return facetFilters;
  }

  buildNumericFilters() {
    const numericFilters = [];

    if (this.activeFilters.priceMin !== null) {
      numericFilters.push(`price>=${this.activeFilters.priceMin}`);
    }
    if (this.activeFilters.priceMax !== null) {
      numericFilters.push(`price<=${this.activeFilters.priceMax}`);
    }

    return numericFilters;
  }

  escapeFilterValue(value) {
    if (typeof value !== 'string') return value;
    return value
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  getInitialPage() {
    return this.initialPage;
  }

  resetInitialPage() {
    this.initialPage = 0;
  }
}

// UI Manager - Handles all DOM updates and rendering
class UIManager {
  constructor() {
    this.updateCurrentStore();
    this.productCardRenderer = null;
  }

  renderResults(results, page = 0, hitsPerPage = 36) {
    this.renderProductGrid(results.groupedHits, results.queryID, page, hitsPerPage);
    this.showSizeTool(results.groupedHits);
    this.renderFacets(results);
    this.updateProductCount(results.nbHits);
    this.updatePagination(results.page, results.nbPages);
  }

  renderProductGrid(products, queryID = null, page = 0, hitsPerPage = 36) {
    // Cache the product grid element
    if (!this.cachedProductGrid) {
      this.cachedProductGrid = document.querySelector('#ProductsList');
    }
    const grid = this.cachedProductGrid;
    if (!grid) return;

    // Hide shimmer state when rendering starts
    this.hideShimmerState();

    // Handle empty results
    if (!products || products.length === 0) {
      grid.innerHTML = '';
      grid.classList.remove('loading-animation');
      return;
    }

    this.initializeProductCardRenderer();

    // Use DocumentFragment for batched DOM updates
    const fragment = document.createDocumentFragment();

    //99B 20250929 -PO, pass on the product index and query id for use in app insights
    products.forEach((product, index) => {
      const transformedProduct = this.transformAlgoliaToProduct(product);
      const card = this.createProductCard(
        transformedProduct,
        queryID,
        page * hitsPerPage + index + 1
      );
      fragment.appendChild(card);
    });

    // Single DOM update - replaces innerHTML and appendChild loop
    grid.replaceChildren(fragment);
    grid.classList.remove('loading-animation');

    this.initializeStockStatus();
    this.initializeRatingSnippet();

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  showSizeTool(products) {
    const sizeTool = document.querySelector('[data-sizing-tool]');
    if (!sizeTool) return;

    const hasBike = products.some(product =>
      product.variants.some(variant => variant.meta?.sizing_guide ?? false)
    );

    sizeTool.classList.toggle('hidden', !hasBike);
    const container = sizeTool.querySelector('.twp-sizing-tool');

    if (container) {
      container.classList.remove('loading-animation');
    }
  }

  showShimmerState() {
    // Cache the product grid element
    if (!this.cachedProductGrid) {
      this.cachedProductGrid = document.querySelector('#ProductsList');
    }
    const grid = this.cachedProductGrid;
    if (!grid) return;

    // Create shimmer skeleton HTML
    const shimmerHTML = this.createShimmerHTML();

    // Replace grid content with shimmer - don't add loading-animation class as it hides content
    grid.innerHTML = shimmerHTML;
    grid.classList.add('shimmer-loading');
  }

  hideShimmerState() {
    // Cache the product grid element
    if (!this.cachedProductGrid) {
      this.cachedProductGrid = document.querySelector('#ProductsList');
    }
    const grid = this.cachedProductGrid;
    if (!grid) return;

    // Remove shimmer classes - actual content will be added by renderProductGrid
    grid.classList.remove('loading-animation', 'shimmer-loading');
  }

  createShimmerHTML() {
    // Create 12 shimmer cards with simplified structure - focus on key visual elements
    return Array.from(
      { length: 12 },
      () => `
      <div class="shimmer-product-card product-card product-card-style-standard f-column">
        <div class="shimmer-product-card__wrapper product-card__wrapper h-full">
          <div class="shimmer-product-card__image-wrapper product-card__image-wrapper product-card__image-wrapper--main-only">
            <div class="shimmer-product-card__image no-empty"></div>
            <div class="shimmer-badge no-empty"></div>
          </div>
          <div class="shimmer-product-card__info product-card__info text-left">
            <div class="shimmer-element shimmer-vendor no-empty"></div>
            <div class="shimmer-element shimmer-title no-empty"></div>
            <div class="shimmer-element shimmer-title-line2 no-empty"></div>
            <div class="shimmer-element shimmer-price no-empty"></div>
          </div>
          <div class="shimmer-element shimmer-atc-button no-empty"></div>
        </div>
      </div>
    `
    ).join('');
  }

  initializeProductCardRenderer() {
    if (!window.productCardRenderer) {
      window.productCardRenderer = new ProductCardRenderer({
        showQuickView: false,
        showQuickAdd: true,
        cardStyle: 'standard',
        saleBadgeType: 'show_percentage',
        showVendor: true,
        showType: false,
        showPrice: true,
        contentAlignment: 'left',
      });
    }
    this.productCardRenderer = window.productCardRenderer;
  }

  createProductCard(product, queryID = null, index = 0) {
    const card = document.createElement('div');
    card.classList.add('card');
    card.setAttribute('data-product-grid-item', '');
    card.innerHTML = this.productCardRenderer.render(product, {
      sectionId: 'algolia-search',
      queryID: queryID,
      index: index,
    });
    return card;
  }

  transformAlgoliaToProduct(algoliaResult) {
    const mainVariant = algoliaResult.variants[0];

    return {
      id: mainVariant.id,
      title: mainVariant.title,
      sku: mainVariant.sku,
      vendor: mainVariant.vendor || '',
      type: mainVariant.product_type || '',
      url: mainVariant.handle ? `/products/${mainVariant.handle}` : '#',
      handle: mainVariant.handle,
      featured_image: this.transformImage(mainVariant.image, mainVariant.title),
      images: mainVariant.image ? [this.transformImage(mainVariant.image, mainVariant.title)] : [],
      variants: this.transformVariants(algoliaResult.variants),
      options: this.transformOptions(algoliaResult.variants, mainVariant),
      available: algoliaResult.variants.some(v => v.inventory_available),
      tags: mainVariant.tags || [],
      has_only_default_variant:
        algoliaResult.variants[0].variant_title === 'Default Title' ||
        algoliaResult.variants[0].option1 === 'Default Title',
      selected_or_first_available_variant: this.getSelectedVariant(algoliaResult.variants),
      metafields: mainVariant.meta || {},
      collections: mainVariant.collections || [],
      object_id: algoliaResult.objectID,
    };
  }

  transformImage(imageUrl, title) {
    return imageUrl
      ? {
          url: imageUrl,
          alt: title || '',
          width: 800,
          height: 600,
          aspect_ratio: 4 / 3,
        }
      : null;
  }

  transformVariants(variants) {
    return variants.map(variant => ({
      id: variant.objectID,
      title: variant.variant_title || variant.option1 || 'Default',
      price: variant.price * 100,
      compare_at_price: variant.compare_at_price ? variant.compare_at_price * 100 : null,
      available: variant.inventory_available,
      metafields: variant.meta || {},
      option1: variant.option1,
    }));
  }

  transformOptions(variants, mainVariant) {
    if (!mainVariant.option_names) return [];

    return mainVariant.option_names.map(optionName => ({
      name: optionName,
      values: [...new Set(variants.map(v => v.options?.[optionName] || v.option1 || 'Default'))],
    }));
  }

  getSelectedVariant(variants) {
    const selectedVariant = variants.find(v => v.inventory_available) || variants[0];
    return {
      ...selectedVariant,
      price: selectedVariant.price * 100,
      compare_at_price: selectedVariant.compare_at_price
        ? selectedVariant.compare_at_price * 100
        : null,
    };
  }

  renderFacets(results) {
    const facetsContainer = document.querySelector('#FacetFiltersContainer');
    if (!facetsContainer) return;

    const facetRenderer = new FacetRenderer(this.currentStore,this.currentStoreKey);
    const facetsHTML = facetRenderer.renderFacets(results);
    const facetWrapper = facetsContainer.querySelector('[data-facet-wrapper]');

    this.updateFacetsContainer(facetsContainer, facetsHTML);

    // Apply active filters to checkboxes and setup event handlers
    this.applyActiveFiltersToDOM();
    this.setupFacetEventHandlers();
    this.restorePriceRangeValues();

    if (facetWrapper) {
      facetWrapper.classList.remove('loading-animation');
    }

    // Setup form synchronization
    setTimeout(() => {
      this.addSyncBetweenForms();
    }, 100);
  }

  updateFacetsContainer(container, facetsHTML) {
    const facetsElement = container.querySelector('.sticky-element');
    if (facetsElement) {
      facetsElement.innerHTML = `
        <form class="face-form" method="GET" action="${window.location.pathname}"
              is="facet-form" data-section-id="algolia-search" data-history update-on-change>
          ${facetsHTML}
        </form>
      `;
    }

    // Update secondary container if exists
    const secondaryContainer = document.querySelector(
      '.face-form:not(#FacetFiltersContainer .face-form)'
    );
    if (secondaryContainer) {
      secondaryContainer.innerHTML = facetsHTML;
    }
  }

  applyActiveFiltersToDOM() {
    const activeFilters = window.algoliaSearch.filterManager.getActiveFilters();
    const forms = document.querySelectorAll('.face-form');

    forms.forEach(form => {
      // Apply facet filters to checkboxes
      Object.entries(activeFilters.facets).forEach(([facetName, values]) => {
        const checkboxes = form.querySelectorAll(`input[name="filter.v.${facetName}"]`);

        checkboxes.forEach(checkbox => {
          const shouldBeChecked = values.some(value => {
            // Handle both encoded and decoded values
            const checkboxValue = checkbox.value
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
            const filterValue = value
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
            return checkboxValue === filterValue || checkbox.value === value;
          });

          checkbox.checked = shouldBeChecked;
        });
      });
    });
  }

  restorePriceRangeValues() {
    const activeFilters = window.algoliaSearch.filterManager.getActiveFilters();
    if (activeFilters.priceMin === null && activeFilters.priceMax === null) return;

    const priceRanges = document.querySelectorAll('price-range');
    priceRanges.forEach(priceRange => {
      if (priceRange.hasAttribute('data-initialized')) return;

      const minInput = priceRange.querySelector('input[name="filter.v.price.gte"]');
      const maxInput = priceRange.querySelector('input[name="filter.v.price.lte"]');
      const minRange = priceRange.querySelector('input[type="range"]:first-child');
      const maxRange = priceRange.querySelector('input[type="range"]:last-child');

      if (minInput && maxInput && minRange && maxRange) {
        if (activeFilters.priceMin !== null) {
          const minValue = Math.floor(activeFilters.priceMin);
          minInput.value = minValue;
          minRange.value = minValue;
        }

        if (activeFilters.priceMax !== null) {
          const maxValue = Math.ceil(activeFilters.priceMax);
          maxInput.value = maxValue;
          maxRange.value = maxValue;
        }

        // Update visual range
        const rangeWrapper = priceRange.querySelector('.facet-range-wrapper');
        if (rangeWrapper) {
          const rangeMinValue = parseInt(minRange.min);
          const rangeMaxValue = parseInt(maxRange.max);
          const rangeSpan = rangeMaxValue - rangeMinValue;
          const minPercent = ((parseInt(minRange.value) - rangeMinValue) / rangeSpan) * 100;
          const maxPercent = ((parseInt(maxRange.value) - rangeMinValue) / rangeSpan) * 100;
          rangeWrapper.style.setProperty('--range-min', `${minPercent}%`);
          rangeWrapper.style.setProperty('--range-max', `${maxPercent}%`);
        }

        priceRange.setAttribute('data-initialized', 'true');
      }
    });
  }

  setupFacetEventHandlers() {
    const forms = document.querySelectorAll('.face-form');

    forms.forEach(form => {
      // Setup price range handlers (these need individual setup on every render)
      const priceRanges = form.querySelectorAll('price-range');
      priceRanges.forEach(priceRange => {
        // Only setup if not already initialized
        if (!priceRange.hasAttribute('data-handlers-initialized')) {
          this.setupPriceRangeHandlers(priceRange);
          priceRange.setAttribute('data-handlers-initialized', 'true');
        }
      });

      // Setup active filter removal handlers
      this.setupActiveFilterHandlers(form);
    });
  }

  handleCheckboxChange(event) {
    const checkbox = event.target;
    const facetName = checkbox.name.replace('filter.v.', '');

    // Update filter manager
    window.algoliaSearch.filterManager.updateFacetFilter(checkbox);

    // Execute search
    window.algoliaSearch.executeSearch();
  }

  setupPriceRangeHandlers(priceRange) {
    const minRange = priceRange.querySelector('.facet-range__min');
    const maxRange = priceRange.querySelector('.facet-range__max');
    const inputMin = priceRange.querySelector('input[name="filter.v.price.gte"]');
    const inputMax = priceRange.querySelector('input[name="filter.v.price.lte"]');
    const rangeWrapper = priceRange.querySelector('.facet-range-wrapper');

    if (!minRange || !maxRange || !inputMin || !inputMax || !rangeWrapper) return;

    const updateRangeProgress = () => {
      const min = parseInt(minRange.value);
      const max = parseInt(maxRange.value);
      const rangeMinValue = parseInt(minRange.min);
      const rangeMaxValue = parseInt(maxRange.max);
      const rangeSpan = rangeMaxValue - rangeMinValue;

      const minPercent = ((min - rangeMinValue) / rangeSpan) * 100;
      const maxPercent = ((max - rangeMinValue) / rangeSpan) * 100;

      rangeWrapper.style.setProperty('--range-min', `${minPercent}%`);
      rangeWrapper.style.setProperty('--range-max', `${maxPercent}%`);
    };

    const applyPriceFilter = () => {
      window.algoliaSearch.filterManager.activeFilters.priceMin = parseInt(inputMin.value);
      window.algoliaSearch.filterManager.activeFilters.priceMax = parseInt(inputMax.value);
      window.algoliaSearch.executeSearch();
    };

    // Note: Event listeners are now only added once per element due to data-handlers-initialized check

    // Input handlers
    inputMin.addEventListener('change', () => {
      const value = parseInt(inputMin.value);
      const max = parseInt(inputMax.value);
      if (value > max) inputMin.value = max;
      minRange.value = inputMin.value;
      updateRangeProgress();
      applyPriceFilter();
    });

    inputMax.addEventListener('change', () => {
      const value = parseInt(inputMax.value);
      const min = parseInt(inputMin.value);
      if (value < min) inputMax.value = min;
      maxRange.value = inputMax.value;
      updateRangeProgress();
      applyPriceFilter();
    });

    // Range handlers
    minRange.addEventListener('input', () => {
      inputMin.value = minRange.value;
      updateRangeProgress();
    });
    minRange.addEventListener('change', applyPriceFilter);

    maxRange.addEventListener('input', () => {
      inputMax.value = maxRange.value;
      updateRangeProgress();
    });
    maxRange.addEventListener('change', applyPriceFilter);
  }

  resetPriceRangeUI() {
    const priceRanges = document.querySelectorAll('price-range');
    priceRanges.forEach(priceRange => {
      const minRange = priceRange.querySelector('input[type="range"]:first-child');
      const maxRange = priceRange.querySelector('input[type="range"]:last-child');
      const minInput = priceRange.querySelector('input[name="filter.v.price.gte"]');
      const maxInput = priceRange.querySelector('input[name="filter.v.price.lte"]');

      if (minRange && maxRange && minInput && maxInput) {
        minRange.value = minRange.min;
        maxRange.value = maxRange.max;
        minInput.value = minRange.min;
        maxInput.value = maxRange.max;

        const rangeWrapper = priceRange.querySelector('.facet-range-wrapper');
        if (rangeWrapper) {
          rangeWrapper.style.setProperty('--range-min', '0%');
          rangeWrapper.style.setProperty('--range-max', '100%');
        }

        priceRange.removeAttribute('data-initialized');
      }
    });
  }

  setupActiveFilterHandlers(form) {
    // Clear individual facet filter
    const facetClearButtons = form.querySelectorAll('[data-clear-facet-filter]');
    facetClearButtons.forEach(button => {
      button.removeEventListener('click', this.handleClearFacetFilter);
      button.addEventListener('click', this.handleClearFacetFilter.bind(this));
    });

    // Clear price filter
    const priceClearButtons = form.querySelectorAll('[data-clear-price-filter]');
    priceClearButtons.forEach(button => {
      button.removeEventListener('click', this.handleClearPriceFilter);
      button.addEventListener('click', this.handleClearPriceFilter.bind(this));
    });

    // Clear all filters
    const clearAllButtons = form.querySelectorAll('[data-clear-all-filters]');
    clearAllButtons.forEach(button => {
      button.removeEventListener('click', this.handleClearAllFilters);
      button.addEventListener('click', this.handleClearAllFilters.bind(this));
    });
  }

  handleClearFacetFilter(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const facetName = button.getAttribute('data-clear-facet-filter');
    const filterValue = button.getAttribute('data-filter-value');

    // Escape the filter value for use in CSS selector
    const escapedFilterValue = CSS.escape(filterValue);

    // Find and uncheck the corresponding checkbox
    const checkbox = document.querySelector(
      `input[name="filter.v.${facetName}"][value="${escapedFilterValue}"]`
    );
    if (checkbox) {
      checkbox.checked = false;
      // Trigger change event to update filters
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  handleClearPriceFilter(event) {
    event.preventDefault();

    // Clear price filters in FilterManager
    window.algoliaSearch.filterManager.activeFilters.priceMin = null;
    window.algoliaSearch.filterManager.activeFilters.priceMax = null;

    // Reset price range UI
    this.resetPriceRangeUI();

    // Execute search
    window.algoliaSearch.executeSearch();
  }

  handleClearAllFilters(event) {
    event.preventDefault();

    // Clear all filters in FilterManager
    window.algoliaSearch.filterManager.activeFilters.facets = {};
    window.algoliaSearch.filterManager.activeFilters.priceMin = null;
    window.algoliaSearch.filterManager.activeFilters.priceMax = null;

    // Uncheck all checkboxes
    const checkboxes = document.querySelectorAll('.face-form input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Reset price range UI
    this.resetPriceRangeUI();

    // Execute search
    window.algoliaSearch.executeSearch();
  }

  addSyncBetweenForms() {
    const primaryForm = document.querySelector('#FacetFiltersContainer .face-form');
    const secondaryForm = document.querySelector(
      '.face-form:not(#FacetFiltersContainer .face-form)'
    );

    if (!primaryForm || !secondaryForm) return;

    // Remove existing sync attributes to prevent double binding
    if (primaryForm.hasAttribute('data-sync-added')) {
      return;
    }

    primaryForm.setAttribute('data-sync-added', 'true');
    secondaryForm.setAttribute('data-sync-added', 'true');

    // Add a global document listener to catch all filter changes
    document.addEventListener(
      'change',
      event => {
        const target = event.target;

        // Only handle checkbox, radio, select, and range inputs
        if (!['checkbox', 'radio', 'select-one', 'range'].includes(target.type)) {
          return;
        }

        // Check if the changed element is within either form
        const isInPrimaryForm = primaryForm.contains(target);
        const isInSecondaryForm = secondaryForm.contains(target);

        if (!isInPrimaryForm && !isInSecondaryForm) {
          return;
        }

        // Skip if this was triggered by our own sync code
        if (event.syncProcessed) {
          return;
        }

        // Determine source and target forms
        const sourceForm = isInPrimaryForm ? primaryForm : secondaryForm;
        const targetForm = isInPrimaryForm ? secondaryForm : primaryForm;

        // Find matching input in target form
        let selector;
        if (target.type === 'select-one') {
          selector = `select[name="${target.name}"]`;
        } else if (target.type === 'range') {
          selector = `input[type="range"][id="${target.id}"]`;
        } else {
          // Escape the value for use in CSS selector
          const escapedValue = CSS.escape(target.value);
          selector = `input[name="${target.name}"][value="${escapedValue}"]`;
        }

        const targetInput = targetForm.querySelector(selector);
        if (!targetInput) return;

        // Update target input
        if (target.type === 'checkbox' || target.type === 'radio') {
          targetInput.checked = target.checked;
        } else {
          targetInput.value = target.value;
        }

        // Dispatch a custom event on the target input
        const customEvent = new Event('change', { bubbles: true });
        customEvent.syncProcessed = true; // Add flag to prevent infinite loop
        targetInput.dispatchEvent(customEvent);

        // For range inputs, also trigger input event to update visual styling
        if (target.type === 'range') {
          targetInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      },
      true
    ); // Use capture to get the event before other handlers
  }

  updateProductCount(count) {
    const productCountElement = document.getElementById('ProductCount');
    if (productCountElement) {
      productCountElement.textContent = `${count} results`;
    }
  }

  updatePagination(currentPage, totalPages) {
    const paginationElement = document.querySelector('nav.pagination');
    if (!paginationElement) return;

    if (totalPages <= 1) {
      paginationElement.innerHTML = '';
      return;
    }

    const paginationRenderer = new PaginationRenderer();
    const paginationHTML = paginationRenderer.render(currentPage, totalPages);
    paginationElement.innerHTML = paginationHTML;

    this.setupPaginationHandlers(paginationElement);
  }

  setupPaginationHandlers(paginationElement) {
    const pageLinks = paginationElement.querySelectorAll('a[data-page]');
    pageLinks.forEach(link => {
      link.addEventListener('click', event => {
        event.preventDefault();
        const page = parseInt(link.getAttribute('data-page'));
        window.algoliaSearch.executeSearch(page);
        window.scrollTo(0, 0);
      });
    });
  }

  initializeStockStatus() {
    if (window.ProductCard?.initializeStockStatus) {
      window.ProductCard.initializeStockStatus();
    }
  }

  initializeRatingSnippet() {
    try {
      ratingSnippet('ruk_rating_snippet_card', {
        store: reviewsIoStore,
        color: '#ffd60a',
        linebreak: true,
        usePolaris: true,
        text: '',
      });
    } catch(e) {
      console.error(`Failed to initialise reviews. Error: ${e}`);
    }
  }

  // Add method to update current store
  updateCurrentStore() {
    try {
      this.currentStoreJSON = JSON.parse(localStorage.getItem('userDeliveryStore'));
    } catch(e) {
      console.error("UIManager failed to parse userDeliveryStore json:",e);
      this.currentStoreJSON = null;
    }
    this.currentStore = this.currentStoreJSON?.name;
    //99B 20260309 -PO, change to use location id to get selected store facet key
    const clickAndCollectLocations = window.theme.clickAndCollectLocations || [];
    const currentStoreId = this.currentStoreJSON?.store_reference;
    this.currentStoreKey = clickAndCollectLocations?.find(location => location.location_id === currentStoreId)?.status_key;
  }
}

// URL Manager - Handles URL state management
class URLManager {
  updateURL(filters, page = 0) {
    const url = new URL(window.location.href);

    this.updateSortParam(url, filters.sortBy);
    this.updatePageParam(url, page);
    this.updatePriceParam(url, filters);
    this.updateFacetParams(url, filters.facets);

    window.history.replaceState({}, '', url.toString());
  }

  updateSortParam(url, sortBy) {
    // Only add sort param if it's not the default relevance index
    if (sortBy && sortBy !== 'shopify_products' && sortBy !== 'relevance') {
      url.searchParams.set('sort_by', sortBy);
    } else {
      url.searchParams.delete('sort_by');
    }
  }

  updatePageParam(url, page) {
    if (page > 0) {
      url.searchParams.set('page', page + 1);
    } else {
      url.searchParams.delete('page');
    }
  }

  updatePriceParam(url, filters) {
    // Only add price parameter when it exists AND no facet filters are active
    if (
      (filters.priceMin !== null || filters.priceMax !== null) &&
      Object.keys(filters.facets).length === 0
    ) {
      const minValue = filters.priceMin || 0;
      const maxValue = filters.priceMax || '';
      url.searchParams.set('range[price]', `${minValue}:${maxValue}`);
    } else {
      url.searchParams.delete('range[price]');
    }
  }

  updateFacetParams(url, facets) {
    // Clear existing facet params
    Array.from(url.searchParams.keys()).forEach(key => {
      if (key.startsWith('filter.v.')) {
        url.searchParams.delete(key);
      }
    });

    // Add current facet params
    Object.entries(facets).forEach(([facet, values]) => {
      if (values.length > 0) {
        url.searchParams.set(`filter.v.${facet}`, values.join(','));
      }
    });
  }

  // Add method to remove click and collect filters from URL
  removeClickAndCollectFromURL() {
    const url = new URL(window.location.href);

    // Remove any filter parameters containing click_and_collect
    Array.from(url.searchParams.keys()).forEach(key => {
      if (key.startsWith('filter.v.') && key.includes('search_information.click_and_collect')) {
        url.searchParams.delete(key);
      }
    });

    // Update URL without these parameters
    window.history.replaceState({}, '', url.toString());
  }
}

// Facet Renderer - Handles facet HTML generation
class FacetRenderer {
  constructor(currentStore,currentStoreKey) {
    this.currentStore = currentStore;
    this.currentStoreKey = currentStoreKey;
    this.expandedFilterGroups = this.getExpandedFilterGroups();
  }

  getExpandedFilterGroups() {
    const jsonData = document.querySelector('[data-expand-filter-groups]');
    if (!jsonData) return [];

    try {
      const { expand_filter_groups } = JSON.parse(jsonData.textContent);
      if (!expand_filter_groups) return [];

      return expand_filter_groups.split(',').map(filter => filter.trim().toLowerCase());
    } catch (error) {
      return [];
    }
  }

  hasActiveFacetSelections(facetNameOrTitle) {
    // Access the active filters from the global algolia search instance
    if (!window.algoliaSearch?.filterManager) {
      return false;
    }

    const activeFilters = window.algoliaSearch.filterManager.getActiveFilters();

    // Check if this is a price filter (common titles for price filters)
    const isPriceFilter =
      facetNameOrTitle === 'Price' ||
      facetNameOrTitle === 'price' ||
      facetNameOrTitle.toLowerCase().includes('price');

    if (isPriceFilter) {
      return activeFilters.priceMin !== null || activeFilters.priceMax !== null;
    }

    // For facet filters, check both the exact name and try to find by title
    if (
      activeFilters.facets &&
      activeFilters.facets[facetNameOrTitle] &&
      activeFilters.facets[facetNameOrTitle].length > 0
    ) {
      return true;
    }

    // If not found by direct name, try to find by matching title to facet config
    const facetConfig = window.collectionConfig
      ? window.algoliaShopify.collectionFacets
      : window.algoliaShopify.facets;

    if (facetConfig) {
      const configEntry = facetConfig.find(f => f.title === facetNameOrTitle);
      if (
        configEntry &&
        activeFilters.facets &&
        activeFilters.facets[configEntry.escapedName] &&
        activeFilters.facets[configEntry.escapedName].length > 0
      ) {
        return true;
      }
    }

    return false;
  }

  shouldFacetExpand(facetName) {
    // Check if facet should be expanded based on configuration
    const expandFilterGroups = this.getExpandedFilterGroups();
    const isConfiguredToExpand =
      expandFilterGroups && expandFilterGroups.includes(facetName.toLowerCase());

    // Check if facet has active selections
    const hasActiveSelections = this.hasActiveFacetSelections(facetName);

    return isConfiguredToExpand || hasActiveSelections;
  }

  hasActiveDeliveryFilter(facetName) {
    // Check if delivery filter (value 0) is active for this facet
    const activeFilters = window.algoliaSearch?.filterManager?.getActiveFilters();
    if (!activeFilters?.facets[facetName]) {
      return false;
    }

    return activeFilters.facets[facetName].includes('0');
  }

  renderFacets(results) {
    const facets = results.facets || {};
    const facetsStats = results.facets_stats || {};
    const priceContextStats = results.price_context_stats || null;
    const facetConfig = window.collectionConfig
      ? window.algoliaShopify.collectionFacets
      : window.algoliaShopify.facets;

    let facetsHTML = '';

    // Add active filters at the top
    facetsHTML += this.renderActiveFilters();

    // Create ordered list of facets based on configuration
    const orderedFacetNames = this.getOrderedFacetNames(facets, facetConfig);

    orderedFacetNames.forEach((facetName, index) => {
      const facetValues = facets[facetName];
      if (!facetValues) return; // Skip if no values for this facet

      const config = facetConfig?.find(f => f.escapedName === facetName) || {
        title: facetName,
        type: 'conjunctive',
      };

      // Determine CSS order - store filters always at top (order: 0), others follow config order
      const isStoreFilter = facetName.includes('search_information.click_and_collect');
      const isDeliveryFilter = facetName.includes('stock_status.delivery_status');
      const cssOrder = isStoreFilter || isDeliveryFilter  ? 0 : index + 1;

      if (config.type === 'slider' || facetName === 'price') {
        facetsHTML += this.renderPriceRange(
          facetsStats[facetName],
          config.title,
          cssOrder,
          priceContextStats
        );
      } else {
        facetsHTML += this.renderFacetCheckboxes(
          facetValues,
          config.title,
          facetName,
          cssOrder,
          results.nbHits
        );
      }
    });

    return facetsHTML;
  }

  getOrderedFacetNames(facets, facetConfig) {
    const availableFacetNames = Object.keys(facets);
    const orderedFacets = [];

    // First, add facets in the order they appear in the configuration
    if (facetConfig) {
      facetConfig.forEach(config => {
        if (config.enabled && availableFacetNames.includes(config.escapedName)) {
          orderedFacets.push(config.escapedName);
        }
      });
    }

    // Then add any remaining facets that aren't in the configuration
    availableFacetNames.forEach(facetName => {
      if (!orderedFacets.includes(facetName)) {
        orderedFacets.push(facetName);
      }
    });

    // Sort to ensure store filters (click_and_collect) come first
    return orderedFacets.sort((a, b) => {
      const aIsStore = a.includes('search_information.click_and_collect');
      const bIsStore = b.includes('search_information.click_and_collect');

      if (aIsStore && !bIsStore) return -1;
      if (!aIsStore && bIsStore) return 1;

      // If both are store filters or both are regular filters, maintain config order
      return 0;
    });
  }

  renderPriceRange(stats, title, cssOrder = 1, priceContextStats = null) {
    let min = 0;
    let max = stats ? Math.ceil(stats.max) : 1000;
    const filterId = `vertical-price`;

    // Use price context stats for true min/max when price filter is active
    if (priceContextStats) {
      if (priceContextStats.min !== undefined) {
        min = Math.floor(priceContextStats.min);
      }
      if (priceContextStats.max !== undefined) {
        max = Math.ceil(priceContextStats.max);
      }
    }

    // Check if min and max are the same (all products have the same price)
    const isSamePrice = min === max;

    // Hide the entire price range component when all products have the same price
    if (isSamePrice) {
      return '';
    }

    // Get active price filters and adjust them if they fall outside the new range
    const activeFilters = window.algoliaSearch?.filterManager?.getActiveFilters();
    let activeMin = activeFilters?.priceMin;
    let activeMax = activeFilters?.priceMax;

    // Adjust price filters if they fall outside the new range
    if (activeMin !== null && activeMin < min) {
      activeMin = min;
      window.algoliaSearch.filterManager.activeFilters.priceMin = activeMin;
    }
    if (activeMax !== null && activeMax > max) {
      activeMax = max;
      window.algoliaSearch.filterManager.activeFilters.priceMax = activeMax;
    }
    if (activeMin !== null && activeMin > max) {
      activeMin = null;
      window.algoliaSearch.filterManager.activeFilters.priceMin = null;
    }
    if (activeMax !== null && activeMax < min) {
      activeMax = null;
      window.algoliaSearch.filterManager.activeFilters.priceMax = null;
    }

    const stepValue = Math.max(1, Math.floor((max - min) / 100));

    // Generate display text based on active filters
    let priceDisplayText;
    if (activeMin !== null && activeMax !== null) {
      // If min is 0, show "Up to", otherwise show "From X to Y"
      if (activeMin === 0) {
        priceDisplayText = `${window.theme.translations.upTo} $${activeMax}`;
      } else {
        priceDisplayText = `${window.theme.translations.from || 'From'} $${activeMin} ${
          window.theme.translations.to || 'to'
        } $${activeMax}`;
      }
    } else if (activeMin !== null) {
      priceDisplayText = `${window.theme.translations.from || 'From'} $${activeMin}`;
    } else if (activeMax !== null) {
      priceDisplayText = `${window.theme.translations.upTo} $${activeMax}`;
    } else {
      priceDisplayText = `${window.theme.translations.upTo} $${max.toFixed(2)}`;
    }

    return `
      <details is="accordion-details" class="accordion-details facet-accordion-details"
               data-filter data-index="${filterId}" ${
      this.shouldFacetExpand(title) ? 'open' : ''
    } style="order:${cssOrder}">
        <summary class="accordion-details__summary flex items-center justify-between">
          <span class="h4">${title}</span>
          ${this.renderToggleIcon()}
        </summary>
        <div class="accordion-details__content accordion-details__content--price">
          <price-range class="grid">
            <span class="text-subtext">${priceDisplayText}</span>
            ${this.renderPriceRangeInputs(min, max, stepValue, filterId, activeMin, activeMax)}
          </price-range>
        </div>
      </details>
    `;
  }

  renderPriceRangeInputs(min, max, stepValue, filterId, activeMin = null, activeMax = null) {
    // Use the actual min/max values for proper range representation
    const flooredMin = Math.floor(min);
    const ceiledMax = Math.ceil(max);

    // Use adjusted values if provided, otherwise use the range min/max
    const initialMinValue = activeMin !== null ? Math.floor(activeMin) : flooredMin;
    const initialMaxValue = activeMax !== null ? Math.ceil(activeMax) : ceiledMax;

    // For visual styling, the range should span the full width since our inputs now use the actual min/max
    const minPercent = 0; // min slider position should be 0% (start of the actual range)
    const maxPercent = 100; // max slider position should be 100% (end of the actual range)

    // Fix step value calculation to match Liquid logic
    const adjustedStepValue = Math.min(1, Math.ceil((ceiledMax - flooredMin) / 100));

    return `
      <label class="visually-hidden" for="FacetFormRangeFromPrice-${filterId}">From</label>
      <label class="visually-hidden" for="FacetFormRangeToPrice-${filterId}">To</label>
      <div class="facet-range-wrapper relative" style="--range-min: ${minPercent}%; --range-max: ${maxPercent}%;">
        <input class="facet-range facet-range__min w-full pointer-events-none" type="range"
               min="${flooredMin}" max="${ceiledMax}" step="${adjustedStepValue}" value="${initialMinValue}"
               id="FacetFormRangeFromPrice-${filterId}">
        <input class="facet-range facet-range__max w-full pointer-events-none absolute" type="range"
               min="${flooredMin}" max="${ceiledMax}" step="${adjustedStepValue}" value="${initialMaxValue}"
               id="FacetFormRangeToPrice-${filterId}">
      </div>
      <div class="facet-input-wrapper flex items-center gap-4">
        ${this.renderPriceInput(
          'gte',
          flooredMin,
          ceiledMax,
          adjustedStepValue,
          filterId,
          'From',
          initialMinValue
        )}
        ${this.renderPriceInput(
          'lte',
          flooredMin,
          ceiledMax,
          adjustedStepValue,
          filterId,
          'To',
          initialMaxValue
        )}
      </div>
    `;
  }

  renderPriceInput(type, min, max, stepValue, filterId, label, initialValue = null) {
    const value = initialValue !== null ? initialValue : type === 'gte' ? min : max;
    return `
      <div class="flex items-center flex-grow relative gap-2">
        <label class="visually-hidden" for="FacetFormInput${label}Price-${filterId}">${label}</label>
        <span class="facet-range__symbol">$</span>
        <input class="form-control facet-range__input" type="number" inputmode="numeric"
               name="filter.v.price.${type}" id="FacetFormInput${label}Price-${filterId}"
               autocomplete="off" placeholder="${value}" min="${min}" max="${max}"
               step="${stepValue}" value="${value}">
      </div>
    `;
  }

  renderFacetCheckboxes(facetValues, title, facetName, cssOrder = 1, totalResults = null) {
    const filterId = `vertical-${facetName.toLowerCase().replace(/\s+/g, '-')}`;
    const isStoreFilter = filterId.includes('search_information.click_and_collect');
    const isDeliveryFilter = filterId.includes('stock_status.delivery_status');

    // Skip if store filter and title doesn't match current store
    if (isStoreFilter && !filterId.includes(this.currentStoreKey)) {
      return '';
    }

    // Check if facet has only one checkbox and its count is larger than total results
    const facetEntries = Object.entries(facetValues);
    if (totalResults && facetEntries.length === 1) {
      const [, count] = facetEntries[0];
      if (count > totalResults) {
        return ''; // Hide the entire accordion
      }
    }

    // For click_and_collect facets
    // 99B 20251212 -PO - fix for delivery filter. "Delivery" filter was instead set to the filter for unavailable at the selected click & collection location,
    // the comment was "separate delivery (value 4) from other availability options" (value 4 means unavailable for C&C, not available for delivery)
    if (isStoreFilter) {
      let html = '';
      const availabilityValues = {};
      const hiddenValues = {};

      // 99B 20251212 -PO - fix this to remove the unavailable filter. Previous comment was "Separate delivery from availability values",
      // (as assumed value === '4' meant available for delivery, not unavailable for C&C)
      Object.entries(facetValues).forEach(([value, count]) => {
        if (value === '4') {
          hiddenValues[value] = count;
        }else{
          availabilityValues[value] = count;
        }
      });

      // Render availability accordion if there are non-available values
      if (Object.keys(availabilityValues).length > 0) {
        const availabilityTitle = 'Availability';
        const shouldExpandAvailability = this.shouldFacetExpand(availabilityTitle);

        html += `
          <details class="accordion-details facet-accordion-details" is="accordion-details"
                   data-filter data-index="${filterId}" ${
          shouldExpandAvailability ? 'open' : ''
        } style="order: ${cssOrder}">
            <summary class="accordion-details__summary flex items-center justify-between">
              <span class="h4">${availabilityTitle}</span>
              ${this.renderToggleIcon()}
            </summary>
            <div class="accordion-details__content parent-display flex flex-col gap-3">
              <span>Store: ${this.currentStore}</span>
              <ul class="grid gap-3">
                ${this.renderCheckboxList(availabilityValues, facetName, filterId, true)}
                ${this.renderCheckboxList(hiddenValues, facetName, filterId, true, false, true)}
              </ul>
            </div>
          </details>
        `;
      }
      return html;
    }

    //99B 20251212 -PO, add custom delivery filter (0 -> available for delivery, anything else unavailable)
    if (isDeliveryFilter) {
      let html = '';
      const deliveryValues = {};

      Object.entries(facetValues).forEach(([value, count]) => {
        if (value === '0') {
          deliveryValues[value] = count;
        }
      });

      // Render delivery accordion if delivery values exist
      if (Object.keys(deliveryValues).length > 0) {
        const deliveryTitle = 'Delivery';
        // Check if delivery filter (value 4) is active
        const hasActiveDeliveryFilter = this.hasActiveDeliveryFilter(facetName);
        const shouldExpandDelivery =
          this.shouldFacetExpand(deliveryTitle) || hasActiveDeliveryFilter;
        const deliveryFilterId = filterId;

        html += `
          <details class="accordion-details facet-accordion-details" is="accordion-details"
                   data-filter data-index="${deliveryFilterId}" ${
          shouldExpandDelivery ? 'open' : ''
        } style="order: ${cssOrder + 0.1}">
            <summary class="accordion-details__summary flex items-center justify-between">
              <span class="h4">${deliveryTitle}</span>
              ${this.renderToggleIcon()}
            </summary>
            <div class="accordion-details__content parent-display flex flex-col gap-3">
              <ul class="grid gap-3">
                ${this.renderCheckboxList(deliveryValues, facetName, deliveryFilterId, true, true)}
              </ul>
            </div>
          </details>
        `;
      }
      return html;
    }

    // For non-store filters, use original logic
    const categoryTitle = title;
    const shouldExpand = this.shouldFacetExpand(categoryTitle);

    return `
      <details class="accordion-details facet-accordion-details" is="accordion-details"
               data-filter data-index="${filterId}" ${
      shouldExpand ? 'open' : ''
    } style="order: ${cssOrder}">
        <summary class="accordion-details__summary flex items-center justify-between">
          <span class="h4">${categoryTitle}</span>
          ${this.renderToggleIcon()}
        </summary>
        <div class="accordion-details__content parent-display flex flex-col gap-3">
          <ul class="grid gap-3">
            ${this.renderCheckboxList(facetValues, facetName, filterId, false)}
          </ul>
        </div>
      </details>
    `;
  }

  sortFacetValues(facetValues, facetName) {
    const entries = Object.entries(facetValues);
    if (facetName === 'options.size') {
      const sizeOrder = {
        XS: 1,
        SM: 2,
        MD: 3,
        LG: 4,
        XL: 5,
        XXL: 6,
        XXXL: 7,
      };
      // Sort by standard sizes, otherwise put at the end
      return entries.sort(([a], [b]) => (sizeOrder[a] || 999) - (sizeOrder[b] || 999));
    }

    // Sorts values in an appropriate order. E.g. [10cm, 12cm]...
    entries.sort(([a], [b]) => {
      // Strip out any non-numerical numbers
      const numA = parseInt(a.replace(/\D/g, ''), 10);
      const numB = parseInt(b.replace(/\D/g, ''), 10);

      // If both values are valid numbers, sort numerically
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Otherwise, sort alphabetically
      return a.localeCompare(b);
    });

    return entries;
  }

  renderCheckboxList(facetValues, facetName, filterId, isStoreFilter, isDeliveryContext = false, isHidden = false) {
    const entries = this.sortFacetValues(facetValues, facetName);

    return entries
      .map(([value, count]) => {
        const checkboxId = `${filterId}-${encodeURIComponent(value)
          .replace(/%[0-9A-F]{2}/g, '-')
          .toLowerCase()}`;
        let displayValue = isStoreFilter
          ? this.getStoreAvailabilityText(value, isDeliveryContext)
          : this.escapeHtml(value);

        // Special case for boolean values
        if (displayValue == 'true') {
          displayValue = 'Yes';
        } else if (displayValue == 'false') {
          displayValue = 'No';
        }

        return `
        <li class="facet-row flex items-center gap-2 ${isHidden ? "hidden" : ""}">
          <input name="filter.v.${facetName}" value="${this.escapeHtmlAttr(value)}"
                 class="checkbox" id="${checkboxId}" type="checkbox">
          <label for="${checkboxId}" class="facet-label flex flex-grow justify-between items-center gap-1 text-subtext reversed-link">
            <span class="reversed-link__text">${displayValue}</span>
            <span class="count">${count}</span>
          </label>
        </li>
      `;
      })
      .join('');
  }

  getStoreAvailabilityText(value, isDeliveryContext = false) {
    // 99B 20251212 -PO, fix for delivery context and available (not unavailable)
    if (isDeliveryContext && value === '0') {
      return 'Available';
    }

    const availabilityMap = {
      0: 'Available now',
      1: 'Available nearby',
      2: 'Available for preorder',
      3: 'Available for special order',
      4: 'Unavailable',
    };
    return availabilityMap[value] || this.escapeHtml(value);
  }

  renderToggleIcon() {
    return `
      <svg class="icon-plus-toggle icon-plus-toggle--small" aria-hidden="true" focusable="false"
           width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path class="vertical" d="M5 0V10" stroke="currentColor" stroke-width="1"/>
        <path class="horizontal" d="M0 5H10" stroke="currentColor" stroke-width="1"/>
      </svg>
    `;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeHtmlAttr(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  renderActiveFilters() {
    const activeFilters = window.algoliaSearch?.filterManager?.getActiveFilters();
    if (!activeFilters || !window.algoliaSearch.filterManager.hasActiveFilters()) {
      return '';
    }

    const facetConfig = window.collectionConfig
      ? window.algoliaShopify.collectionFacets
      : window.algoliaShopify.facets;

    let activeFiltersHTML = '';
    const activeFiltersList = [];

    // Add price filter if active
    if (activeFilters.priceMin !== null || activeFilters.priceMax !== null) {
      const priceLabel = this.formatPriceRange(activeFilters.priceMin, activeFilters.priceMax);
      activeFiltersList.push({
        type: 'price',
        label: `Price: ${priceLabel}`,
        facetName: 'price',
        value: null,
      });
    }

    // Add facet filters
    Object.entries(activeFilters.facets).forEach(([facetName, values]) => {
      const config = facetConfig?.find(f => f.escapedName === facetName);
      const facetTitle = config?.title || facetName;
      const isStoreFilter = facetName.includes('search_information.click_and_collect');
      const isDeliveryFilter = facetName.includes('stock_status.delivery_status');

      values.forEach(value => {
        // Use availability text for store filters, otherwise use the raw value
        // 99B 20251212 -PO. Fix correctly for delivery filters. 
        // Previous comment was: "For delivery filters (value 4), show as 'Available' instead of 'Unavailable'" - (value 4 is unavailable)
        const displayValue = isStoreFilter || isDeliveryFilter
          ? this.getStoreAvailabilityText(value, isDeliveryFilter)
          : value;

        // Update the facet title for delivery filters
        const effectiveFacetTitle = isDeliveryFilter ? 'Delivery' : facetTitle;

        activeFiltersList.push({
          type: 'facet',
          label: `${effectiveFacetTitle}: ${displayValue}`,
          facetName: facetName,
          value: value,
          facetTitle: effectiveFacetTitle,
        });
      });
    });

    if (activeFiltersList.length === 0) {
      return '';
    }

    activeFiltersHTML = `
      <div class="active-filters" style="order: -1;">
        <div class="active-filters__header flex items-center justify-between mb-4">
          <h4 class="active-filters__title h4">Active Filters</h4>
          ${
            activeFiltersList.length > 1
              ? '<button type="button" class="active-filters__clear-all btn--link" data-clear-all-filters>Clear All</button>'
              : ''
          }
        </div>
        <div class="active-filters__list flex flex-wrap gap-1">
          ${activeFiltersList.map(filter => this.renderActiveFilterItem(filter)).join('')}
        </div>
      </div>
    `;

    return activeFiltersHTML;
  }

  renderActiveFilterItem(filter) {
    const removeAction =
      filter.type === 'price'
        ? 'data-clear-price-filter'
        : `data-clear-facet-filter="${this.escapeHtmlAttr(
            filter.facetName
          )}" data-filter-value="${this.escapeHtmlAttr(filter.value)}"`;

    return `
      <button type="button" class="active-filter-item btn  flex items-center gap-2" ${removeAction}>
        <span class="active-filter-item__label">${this.escapeHtml(filter.label)}</span>
        <svg class="icon icon-close icon--extra-small" viewBox="0 0 20 20" fill="none">
          <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;
  }

  formatPriceRange(min, max) {
    if (min !== null && max !== null) {
      return `$${min} - $${max}`;
    } else if (min !== null) {
      return `From $${min}`;
    } else if (max !== null) {
      return `Up to $${max}`;
    }
    return 'Price Range';
  }
}

// Pagination Renderer - Handles pagination HTML generation
class PaginationRenderer {
  render(currentPage, totalPages) {
    let html =
      '<ul class="pagination__list list-unstyled flex items-center flex-wrap justify-center" role="list">';

    // Previous button
    if (currentPage > 0) {
      html += this.renderPreviousButton(currentPage);
    }

    // Page numbers
    html += this.renderPageNumbers(currentPage, totalPages);

    // Next button
    if (currentPage < totalPages - 1) {
      html += this.renderNextButton(currentPage);
    }

    html += '</ul>';
    return html;
  }

  renderPreviousButton(currentPage) {
    return `
      <li>
        <a href="#" class="pagination__item pagination__item-arrow motion-reduce" data-page="${
          currentPage - 1
        }">
          <svg class="icon icon-caret-left rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
          <span>Previous</span>
        </a>
      </li>
    `;
  }

  renderNextButton(currentPage) {
    return `
      <li>
        <a href="#" class="pagination__item pagination__item--next pagination__item-arrow motion-reduce" data-page="${
          currentPage + 1
        }">
          <span>Next</span>
          <svg class="icon icon-caret-right rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </a>
      </li>
    `;
  }

  renderPageNumbers(currentPage, totalPages) {
    let html = '';
    const { startPage, endPage } = this.calculatePageRange(currentPage, totalPages);

    // First page with ellipsis
    if (startPage > 0) {
      html +=
        '<li><a href="#" class="pagination__item" data-page="0" aria-label="Page 1">1</a></li>';
      if (startPage > 1) {
        html += '<li><span class="pagination__item">…</span></li>';
      }
    }

    // Main page numbers
    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage) {
        html += `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current background-2" aria-current="page" aria-label="Page ${
          i + 1
        }">${i + 1}</a></li>`;
      } else {
        html += `<li><a href="#" class="pagination__item" data-page="${i}" aria-label="Page ${
          i + 1
        }">${i + 1}</a></li>`;
      }
    }

    // Last page with ellipsis
    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        html += '<li><span class="pagination__item">…</span></li>';
      }
      html += `<li><a href="#" class="pagination__item" data-page="${
        totalPages - 1
      }" aria-label="Page ${totalPages}">${totalPages}</a></li>`;
    }

    return html;
  }

  calculatePageRange(currentPage, totalPages) {
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    // Ensure we show up to 5 pages when possible
    if (endPage - startPage < 4) {
      if (startPage === 0) {
        endPage = Math.min(4, totalPages - 1);
      } else if (endPage === totalPages - 1) {
        startPage = Math.max(0, totalPages - 5);
      }
    }

    return { startPage, endPage };
  }
}

// Custom Elements (keeping existing functionality but optimized)
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.minRangeInput = null;
    this.maxRangeInput = null;
    this.minPriceInput = null;
    this.maxPriceInput = null;
  }

  connectedCallback() {
    this.initializeElements();
    if (this.isValidSetup()) {
      this.setupEventListeners();
    }
  }

  initializeElements() {
    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');
  }

  isValidSetup() {
    return this.minRangeInput && this.maxRangeInput && this.minPriceInput && this.maxPriceInput;
  }

  setupEventListeners() {
    // Focus handlers
    this.minPriceInput.addEventListener('focus', () => this.minPriceInput.select());
    this.maxPriceInput.addEventListener('focus', () => this.maxPriceInput.select());

    // Change handlers
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));
    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));

    // Input handlers for live updates
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    const value = this.constrainValue(
      event.target.value,
      event.target.min,
      this.maxPriceInput.value
    );
    event.target.value = value;
    this.minRangeInput.value = value;
    this.updateRangeStyle();
    this.applyFilter();
  }

  handleInputMaxChange(event) {
    const value = this.constrainValue(
      event.target.value,
      this.minPriceInput.value,
      event.target.max
    );
    event.target.value = value;
    this.maxRangeInput.value = value;
    this.updateRangeStyle();
    this.applyFilter();
  }

  handleRangeMinChange(event) {
    this.minPriceInput.value = event.target.value;
    this.applyFilter();
  }

  handleRangeMaxChange(event) {
    this.maxPriceInput.value = event.target.value;
    this.applyFilter();
  }

  handleRangeMinInput(event) {
    const maxValue = parseInt(this.maxPriceInput.value);
    // Allow min to reach max value, but not exceed it
    event.target.value = Math.min(parseInt(event.target.value), maxValue);
    this.minPriceInput.value = event.target.value;
    this.updateRangeStyle();
  }

  handleRangeMaxInput(event) {
    const minValue = parseInt(this.minPriceInput.value);
    // Allow max to match min value, but not go below it
    event.target.value = Math.max(parseInt(event.target.value), minValue);
    this.maxPriceInput.value = event.target.value;
    this.updateRangeStyle();
  }

  constrainValue(value, min, max) {
    return Math.max(parseInt(min), Math.min(parseInt(value), parseInt(max)));
  }

  updateRangeStyle() {
    const wrapper = this.querySelector('.facet-range-wrapper');
    if (!wrapper) return;

    const minValue = parseInt(this.minRangeInput.value);
    const maxValue = parseInt(this.maxRangeInput.value);
    const rangeMinValue = parseInt(this.minRangeInput.min);
    const rangeMaxValue = parseInt(this.minRangeInput.max);
    const rangeSpan = rangeMaxValue - rangeMinValue;

    const minPercent = ((minValue - rangeMinValue) / rangeSpan) * 100;
    const maxPercent = ((maxValue - rangeMinValue) / rangeSpan) * 100;

    wrapper.style.setProperty('--range-min', `${minPercent}%`);
    wrapper.style.setProperty('--range-max', `${maxPercent}%`);
  }

  applyFilter() {
    if (window.algoliaSearch) {
      window.algoliaSearch.executeSearch();
    }
  }
}

// Keep existing custom elements with minimal changes
class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';
  }

  connectedCallback() {
    this.initLayoutMode();
    this.buttons.forEach(button => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  onButtonClick(evt) {
    const target = evt.currentTarget || evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (!FoxTheme?.config?.hasLocalStorage) return;

    const layoutMode = window.localStorage.getItem(this.cookieName);
    if (layoutMode) {
      const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
      if (target) this.changeLayout(target, layoutMode);
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;
    this.updateProductCards(productGrid, layoutMode);
    this.updateButtonStates(target);

    if (FoxTheme?.config?.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }

  updateProductCards(productGrid, layoutMode) {
    if (!productGrid.dataset.gridClass) return;

    const productCards = productGrid.querySelectorAll('.product-card');
    productCards.forEach(card => {
      if (layoutMode === 'grid') {
        card.classList.add(productGrid.dataset.gridClass);
      } else {
        card.classList.remove(productGrid.dataset.gridClass);
      }
    });
  }

  updateButtonStates(activeButton) {
    this.buttons.forEach(button => button.classList.remove('btn--active'));
    activeButton.classList.add('btn--active');
  }
}

class FacetToggler extends HTMLButtonElement {
  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClick.bind(this));
  }

  onClick(evt) {
    evt.preventDefault();
    this.facetEl?.classList.toggle('xl:block');
  }
}

// Register custom elements
customElements.define('price-range', PriceRange);
customElements.define('layout-switcher', LayoutSwitcher);
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

// Initialize the application
document.addEventListener('DOMContentLoaded', async () => {
  window.algoliaSearch = new AlgoliaSearch();
  await window.algoliaSearch.init();
});
